/**
 * Entity Domain Configuration
 *
 * Shared configuration for grouping entities by domain (Market, Business, Solution, Insights).
 * Used by Documents3Page and UnifiedDocumentPanel for consistent entity display.
 */

import { TrendingUp, Building2, Lightbulb, Target, type LucideIcon } from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

export interface DomainConfig {
  icon: LucideIcon
  color: string
  bgColor: string
  textColor: string
  borderColor: string
  types: string[]
}

export type DomainKey = 'Market' | 'Business' | 'Solution' | 'Insights'

// ============================================================================
// DOMAIN CONFIGURATION
// ============================================================================

export const DOMAIN_CONFIG: Record<DomainKey, DomainConfig> = {
  Market: {
    icon: TrendingUp,
    color: 'var(--color-market)',
    bgColor: 'bg-market/10',
    textColor: 'text-market',
    borderColor: 'border-market/30',
    types: [
      'Company', 'Industry', 'Competitor', 'CustomerSegment', 'Persona',
      'Person', 'Stakeholder', 'Problem', 'CustomerPain', 'CustomerGain',
      'CustomerJob', 'Alternative', 'Trend', 'MarketSignal',
    ],
  },
  Business: {
    icon: Building2,
    color: 'var(--color-business)',
    bgColor: 'bg-business/10',
    textColor: 'text-business',
    borderColor: 'border-business/30',
    types: [
      'ValueProposition', 'Channel', 'RevenueStream', 'KeyResource',
      'KeyActivity', 'KeyPartnership', 'CostStructure', 'Strategy', 'StrategicGoal',
    ],
  },
  Solution: {
    icon: Lightbulb,
    color: 'var(--color-solution)',
    bgColor: 'bg-solution/10',
    textColor: 'text-solution',
    borderColor: 'border-solution/30',
    types: ['Product', 'Feature', 'Requirement', 'Constraint'],
  },
  Insights: {
    icon: Target,
    color: 'var(--color-text-secondary)',
    bgColor: 'bg-warm-100',
    textColor: 'text-text-secondary',
    borderColor: 'border-warm-300',
    types: [], // Catch-all for other types
  },
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get the domain for an entity type
 */
export function getEntityDomain(entityType: string): DomainKey {
  for (const [domain, config] of Object.entries(DOMAIN_CONFIG)) {
    if (config.types.includes(entityType)) {
      return domain as DomainKey
    }
  }
  return 'Insights'
}

/**
 * Get domain config for an entity type
 */
export function getDomainConfig(entityType: string): DomainConfig {
  return DOMAIN_CONFIG[getEntityDomain(entityType)]
}
